import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import React, { FC } from 'react';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import { IApiResponse } from '../../../types/IApiResponse';
import { IItem } from '../../../types/IItem';
import { IWarehouseItem } from '../../../types/IWarehouse';
import './styles.css';

type TWarehouseResponse = IApiResponse<IWarehouseItem[]>;

export const PartsList: FC<{
  savedInventoryItemsForCurrentTicket?: IWarehouseItem[];
  availableInventoryItemsForCurrentTicket?: TWarehouseResponse;
  canAddMoreParts: boolean;
  setDelayedRequestGetWarehouseItemsByCompanyId: (val: boolean) => void;
  setValue: UseFormSetValue<IItem>;
  removeField: (val: number) => void;
  partsFields: string[];
  getValues: UseFormGetValues<IItem>;
  control: Control<IItem>;
  resetField: UseFormResetField<IItem>;
  errors: FieldErrors<IItem>;
  setPartsFields: React.Dispatch<React.SetStateAction<string[]>>;
  t: (key: string) => string;
  warehouseDataFiltered: IWarehouseItem[] | undefined;
}> = ({
  savedInventoryItemsForCurrentTicket,
  availableInventoryItemsForCurrentTicket,
  partsFields,
  getValues,
  control,
  setValue,
  resetField,
  errors,
  removeField,
  setPartsFields,
  t,
  warehouseDataFiltered,
  canAddMoreParts,
  setDelayedRequestGetWarehouseItemsByCompanyId,
}) => {
  const getUsedInventoryCountForTicket = (
    warehouseItem?: IWarehouseItem,
    savedItems: IWarehouseItem[] = [],
  ): number => {
    if (!warehouseItem) return 0;

    const matchedItem = savedItems.find((item) => item.id === warehouseItem.id);
    return matchedItem?.used_count_this_ticket ?? 0;
  };

  const getOptionQuantityMax = (option: IWarehouseItem): number =>
    option.quantity -
    (option.quantity_used -
      getUsedInventoryCountForTicket(
        option,
        savedInventoryItemsForCurrentTicket,
      ));

  return (
    <>
      <Box component="fieldset" className="ticket-container">
        <Typography component="legend" sx={{ px: 1, fontSize: 12, mb: 0 }}>
          {t('editTicket.added_parts')}

          <Tooltip title={t('editTicket.note_affect_final_payment')}>
            <IconButton color="inherit" size="small">
              <HelpOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Typography>

        <Link
          target="_blank"
          rel="noopener noreferrer"
          href="/warehouse"
          className="ticket-label"
        >
          {t('editTicket.manage_warehouse')}
        </Link>

        {/* Parts content */}

        {availableInventoryItemsForCurrentTicket &&
          availableInventoryItemsForCurrentTicket.data &&
          partsFields.map((fieldId, index) => {
            const currentWarehouseData =
              availableInventoryItemsForCurrentTicket.data?.find(
                (item) => item.id === getValues(`parts.${index}.id`),
              );

            const used_count_this_ticket = getUsedInventoryCountForTicket(
              currentWarehouseData,
              savedInventoryItemsForCurrentTicket,
            );

            const quantityUsed = currentWarehouseData?.quantity_used ?? 0;

            const quantity = currentWarehouseData?.quantity ?? 0;

            const selectedQuantityMax =
              quantity - (quantityUsed - used_count_this_ticket);

            return (
              <Grid
                container
                spacing={1}
                sx={{
                  marginTop: '0 !important',
                  marginBottom: '16px !important',
                }}
                key={fieldId}
              >
                <Grid
                  size={
                    currentWarehouseData ? { xs: 6, sm: 6 } : { xs: 11, sm: 11 }
                  }
                >
                  <Controller
                    control={control}
                    name={`parts.${index}.id`}
                    rules={{
                      required: t('form.required'),
                    }}
                    render={({ field: controllerField }) => (
                      <Autocomplete
                        value={currentWarehouseData || null}
                        size="small"
                        freeSolo={false}
                        options={warehouseDataFiltered || []}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => {
                          const quantityMax = getOptionQuantityMax(option);

                          if (option.quantity < 1) {
                            return null;
                          }
                          return (
                            <li {...props}>
                              {option.name}
                              <Typography
                                component="span"
                                sx={{ pl: 1 }}
                                variant="caption"
                              >
                                ({quantityMax}{' '}
                                {t('form.available').toLowerCase()})
                              </Typography>
                            </li>
                          );
                        }}
                        getOptionDisabled={(option) => {
                          const quantityMax = getOptionQuantityMax(option);

                          return quantityMax <= 0;
                        }}
                        onChange={(_, newValue) => {
                          controllerField.onChange(newValue?.id);

                          const currentCount = getValues(
                            `parts.${index}.used_count_this_ticket`,
                          );

                          setValue(
                            `parts.${index}.price_at_use`,
                            newValue?.price_at_use ?? newValue?.retail_price,
                          );

                          setValue(
                            `parts.${index}.used_count_this_ticket`,
                            currentCount ?? 1,
                          );

                          if (currentCount) {
                            resetField(
                              `parts.${index}.used_count_this_ticket`,
                              {
                                defaultValue: 1,
                              },
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            value={params.inputProps.value ?? ''}
                            label={t('editTicket.select_part')}
                            error={!!errors?.parts?.[index]?.id}
                            helperText={errors?.parts?.[index]?.id?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                {currentWarehouseData && (
                  <>
                    <Grid size={{ xs: 2, sm: 2 }}>
                      <Controller
                        control={control}
                        name={`parts.${index}.used_count_this_ticket`}
                        defaultValue={1}
                        rules={{
                          required: `${t('form.required')}`,
                        }}
                        render={({ field: controllerField }) => (
                          <TextField
                            size="small"
                            {...controllerField}
                            type="number"
                            slotProps={{
                              htmlInput: {
                                min: 1,
                                max: selectedQuantityMax,
                              },
                            }}
                            label={t('editTicket.count')}
                            fullWidth
                            error={
                              !!errors?.parts &&
                              !!errors?.parts[index]?.used_count_this_ticket
                            }
                            helperText={
                              errors?.parts?.[index]?.used_count_this_ticket
                                ?.message
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 3, sm: 3 }}>
                      <Controller
                        control={control}
                        defaultValue={currentWarehouseData?.retail_price}
                        name={`parts.${index}.price_at_use`}
                        rules={{
                          required: t('form.required'),
                        }}
                        render={({ field }) => {
                          const inputValue = field.value ?? null;
                          return (
                            <TextField
                              type="number"
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  step: 0.01,
                                },
                              }}
                              label={
                                <Box>
                                  {t('form.price')}
                                  {inputValue && (
                                    <Tooltip
                                      title={
                                        <>
                                          {t('warehouse.field_retail_price')}:{' '}
                                          {currentWarehouseData?.retail_price}{' '}
                                          <br />
                                          {t(
                                            'warehouse.fild_purchase_price',
                                          )}:{' '}
                                          {currentWarehouseData?.purchase_price}
                                        </>
                                      }
                                    >
                                      <IconButton color="inherit">
                                        <HelpOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              variant="outlined"
                              size="small"
                              rows={4}
                              {...field}
                              value={field.value ?? ''}
                              error={
                                !!errors?.parts &&
                                !!errors?.parts[index]?.price_at_use
                              }
                              helperText={
                                !!errors?.parts &&
                                !!errors?.parts[index]?.price_at_use
                                  ? errors?.parts?.[index]?.price_at_use
                                      ?.message
                                  : ''
                              }
                            />
                          );
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 1, sm: 1 }}>
                  <IconButton
                    onClick={() => {
                      setPartsFields((prev) =>
                        prev.filter((item) => item !== fieldId),
                      );
                      removeField(index);
                    }}
                    sx={{
                      '&:hover': {
                        color: 'error.main',
                      },
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}

        {canAddMoreParts && (
          <Box
            sx={{
              marginBottom: 1,
              marginTop: '0 !important',
              textAlign: 'right',
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                setDelayedRequestGetWarehouseItemsByCompanyId(false);
                setPartsFields((prev) => [...prev, uuidv4()]);
              }}
            >
              {t('form.btn_add')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
